import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Block, FlexDiv, palette } from '@awarego-ui/components';
import services from '../../services';
import { useStores } from '../../hooks/use-stores';
import AwareExposeForm from './AwareExposeForm';
import AwareExposeScanner, { ATTACK_STEPS } from './AwareExposeScanner';
import AwareExposeProfile from './AwareExposeProfile';
import AwareExposeAttacks from './AwareExposeAttacks';
import AwareExposeIntro from './AwareExposeIntro';
import HomeViewContainer from '../Home/HomeViewContainer';

const PHASES = {
    INTRO: 'intro',
    FORM: 'form',
    SCANNING: 'scanning',
    PROFILE: 'profile',
    ATTACKS_LOADING: 'attacks_loading',
    ATTACKS: 'attacks',
};

function AwareExpose({ mode = 'public', currentCompany }) {
    const params = useParams();
    const { authStore } = useStores();
    const publicKey = params.publicKey || null;
    const campaignToken = params.campaignToken || null;
    const isCampaign = mode === 'campaign';
    const isAuthenticated = mode === 'authenticated';

    const [phase, setPhase] = useState(
        isAuthenticated ? PHASES.INTRO : PHASES.FORM
    );
    const [config, setConfig] = useState(null);
    const [configError, setConfigError] = useState(null);
    const [profile, setProfile] = useState(null);
    const [emails, setEmails] = useState(null);
    const [runId, setRunId] = useState(null);
    const [error, setError] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [lastRun, setLastRun] = useState(null);
    const [person, setPerson] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [pinningKeys, setPinningKeys] = useState(() => new Set());

    const companyId = useMemo(() => {
        if (isAuthenticated && currentCompany) {
            return currentCompany.company_id;
        }
        return null;
    }, [isAuthenticated, currentCompany]);

    // Load config (allowed domains, branding, optional campaign invite)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                let cfg;
                if (isCampaign) {
                    cfg = await services.AwareExpose.campaignConfig(
                        campaignToken
                    );
                } else if (mode === 'public') {
                    cfg = await services.AwareExpose.publicConfig(publicKey);
                } else {
                    cfg = await services.AwareExpose.config(companyId);
                    if (!cfg || cfg.enabled === false) {
                        setConfigError(
                            'AwareExpose is not enabled for this company.'
                        );
                        return;
                    }
                }
                if (!cancelled) setConfig(cfg);
            } catch (e) {
                if (!cancelled) {
                    setConfigError(
                        e && e.response && e.response.data && e.response.data.error
                            ? e.response.data.error
                            : 'AwareExpose link not available.'
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mode, publicKey, campaignToken, companyId, isCampaign]);

    // For authenticated mode, fetch the latest run on mount.
    useEffect(() => {
        if (mode !== 'authenticated' || !companyId) return;
        let cancelled = false;
        (async () => {
            try {
                const { run } = await services.AwareExpose.lastRun(companyId);
                if (cancelled) return;
                if (run) {
                    setLastRun(run);
                    setRunId(run.id);
                    setProfile({
                        exposure_level: run.exposure_level,
                        exposure_score: run.exposure_score,
                        signals_count: run.signals_count,
                        summary: run.summary,
                        signals: run.signals || [],
                    });
                    setPerson({
                        name: run.full_name,
                        title: run.job_title,
                        email: run.email,
                        links: run.links_json || [],
                    });
                    setEmails(run.attacks || null);
                    const hasAttacks = run.attacks && run.attacks.length;
                    setPhase(
                        hasAttacks
                            ? PHASES.ATTACKS
                            : run.exposure_level
                                ? PHASES.PROFILE
                                : PHASES.INTRO
                    );
                    setActiveTab(hasAttacks ? 'attacks' : 'profile');
                } else {
                    setPhase(PHASES.FORM);
                }
            } catch (e) {
                // No prior run is fine; fall through to form.
                if (!cancelled) setPhase(PHASES.FORM);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mode, companyId]);

    const runScanCall = async (formValues, options = {}) => {
        setError(null);
        setWarnings([]);
        setProfile(null);
        setEmails(null);
        setActiveTab('profile');
        setPhase(PHASES.SCANNING);
        const nicknames = Array.isArray(formValues.nicknames)
            ? formValues.nicknames
                  .map((n) => String(n || '').trim())
                  .filter(Boolean)
            : [];
        const person = {
            name: String(formValues.name || '').trim(),
            title: String(formValues.title || '').trim(),
            email: String(formValues.email || '').trim().toLowerCase(),
            links: (formValues.links || [])
                .map((l) => String(l || '').trim())
                .filter(Boolean),
            nicknames,
        };
        setPerson(person);
        const excludedSignals = Array.isArray(options.excludedSignals)
            ? options.excludedSignals.filter(Boolean)
            : [];
        const payload = {
            name: person.name,
            title: person.title,
            email: person.email,
            links: person.links,
            nicknames,
            excluded_signals: excludedSignals,
        };
        try {
            let res;
            if (isCampaign) {
                res = await services.AwareExpose.campaignScan(
                    campaignToken,
                    payload
                );
            } else if (mode === 'public') {
                res = await services.AwareExpose.publicScan(
                    publicKey,
                    payload
                );
            } else {
                res = await services.AwareExpose.scan(
                    companyId,
                    person,
                    {
                        nicknames,
                        excluded_signals: excludedSignals,
                    }
                );
            }
            if (isAuthenticated && res.runId) setRunId(res.runId);
            if (isCampaign && res.run_id) setRunId(res.run_id);
            setProfile(res.profile);
            setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
            const scanErrors = Array.isArray(res.errors) ? res.errors : [];
            if (scanErrors.length > 0) {
                setError(scanErrors.join(' '));
            }
            setPhase(PHASES.PROFILE);
        } catch (e) {
            setError(extractMessage(e));
            setWarnings(extractWarnings(e));
            setPhase(PHASES.FORM);
        }
    };

    const handleRequalify = (excludedSignals) => {
        if (!person) return;
        runScanCall(person, { excludedSignals });
    };

    const handleTogglePin = async (signal, signalKey, index) => {
        if (mode !== 'authenticated' || !companyId) return;
        if (pinningKeys.has(signalKey)) return;
        setPinningKeys((prev) => {
            const next = new Set(prev);
            next.add(signalKey);
            return next;
        });
        try {
            const wasPinned = !!(signal && signal.pinned);
            let updatedSignal;
            if (wasPinned) {
                if (signal.pin_id != null) {
                    await services.AwareExpose.removePin(
                        companyId,
                        signal.pin_id
                    );
                }
                updatedSignal = {
                    ...signal,
                    pinned: false,
                    pin_id: null,
                };
            } else {
                const res = await services.AwareExpose.addPin(companyId, {
                    source: signal.source || null,
                    text:
                        signal.text ||
                        signal.finding ||
                        signal.detail ||
                        signal.description ||
                        signal.value ||
                        '',
                    weight: signal.weight,
                });
                updatedSignal = {
                    ...signal,
                    pinned: true,
                    pin_id: (res && res.pin && res.pin.id) || null,
                };
            }
            setProfile((prev) => {
                if (!prev || !Array.isArray(prev.signals)) return prev;
                const nextSignals = prev.signals.slice();
                if (
                    index >= 0 &&
                    index < nextSignals.length &&
                    nextSignals[index] === signal
                ) {
                    nextSignals[index] = updatedSignal;
                } else {
                    // Fall back: match by reference or text in case order changed.
                    const idx = nextSignals.findIndex(
                        (x) =>
                            x === signal ||
                            (x &&
                                signal &&
                                String(x.text || '').toLowerCase() ===
                                    String(signal.text || '').toLowerCase())
                    );
                    if (idx >= 0) nextSignals[idx] = updatedSignal;
                }
                return { ...prev, signals: nextSignals };
            });
        } catch (e) {
            setError(extractMessage(e));
        } finally {
            setPinningKeys((prev) => {
                const next = new Set(prev);
                next.delete(signalKey);
                return next;
            });
        }
    };

    const runAttacksCall = async () => {
        setError(null);
        setWarnings([]);
        setPhase(PHASES.ATTACKS_LOADING);
        try {
            let res;
            if (isCampaign) {
                res = await services.AwareExpose.campaignAttacks(
                    campaignToken,
                    { person, profile }
                );
            } else if (mode === 'public') {
                res = await services.AwareExpose.publicAttacks(
                    publicKey,
                    { person, profile }
                );
            } else {
                res = await services.AwareExpose.attacks(
                    companyId,
                    runId
                );
            }
            setEmails(res.emails || []);
            setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
            const attackErrors = Array.isArray(res.errors) ? res.errors : [];
            if (!res.emails || res.emails.length === 0) {
                setError(
                    (attackErrors.length && attackErrors.join(' ')) ||
                        res.error ||
                        'AwareExpose could not generate any attack emails.'
                );
                setPhase(PHASES.PROFILE);
                setActiveTab('profile');
                return;
            }
            setPhase(PHASES.ATTACKS);
            setActiveTab('attacks');
        } catch (e) {
            setError(extractMessage(e));
            setWarnings(extractWarnings(e));
            setPhase(PHASES.PROFILE);
        }
    };

    const handleStart = () => {
        setProfile(null);
        setEmails(null);
        setRunId(null);
        setError(null);
        setWarnings([]);
        setActiveTab('profile');
        setPhase(PHASES.FORM);
    };

    const wideLayout =
        phase === PHASES.PROFILE ||
        phase === PHASES.ATTACKS_LOADING ||
        phase === PHASES.ATTACKS;
    const blockWidth = wideLayout ? 760 : 640;

    const renderInWrapper = (children, width = blockWidth) => {
        if (mode === 'authenticated') {
            return (
                <HomeViewContainer
                    layoutTitle="AwareExpose"
                    code="aware-expose"
                    pageTitle="AwareExpose result"
                    description="Run a public-signal investigation against yourself and review the latest result."
                    content={children}
                />
            );
        }
        return <PageWrapper width={width}>{children}</PageWrapper>;
    };

    if (configError) {
        return renderInWrapper(
            <AwareExposeIntro
                mode={mode}
                error={configError}
                onStart={null}
            />,
            640
        );
    }
    if (!config) {
        return renderInWrapper(
            <AwareExposeIntro mode={mode} loading />,
            640
        );
    }

    return renderInWrapper(
        <>
            {phase === PHASES.INTRO && (
                <AwareExposeIntro
                    mode={mode}
                    onStart={handleStart}
                    lastRun={lastRun}
                />
            )}
            {phase === PHASES.FORM && (
                <AwareExposeForm
                    config={config}
                    defaultEmail={
                        (isCampaign &&
                            config &&
                            config.invite &&
                            config.invite.email) ||
                        (isAuthenticated &&
                            authStore.currentUser &&
                            authStore.currentUser.email)
                    }
                    defaultName={
                        (isCampaign &&
                            config &&
                            config.invite &&
                            config.invite.name) ||
                        (isAuthenticated &&
                            authStore.currentUser &&
                            authStore.currentUser.name) ||
                        ''
                    }
                    error={error}
                    onSubmit={runScanCall}
                />
            )}
            {phase === PHASES.SCANNING && <AwareExposeScanner />}
            {phase === PHASES.ATTACKS_LOADING && (
                <AwareExposeScanner
                    title="Crafting your personalised attack emails"
                    subtitle="Combining your exposure signals into three educational phishing scenarios…"
                    steps={ATTACK_STEPS}
                />
            )}
            {(phase === PHASES.PROFILE || phase === PHASES.ATTACKS) &&
                profile && (
                    <>
                        {phase === PHASES.ATTACKS && (
                            <StepTabs
                                activeTab={activeTab}
                                onChange={setActiveTab}
                                attacksReady={!!emails && emails.length > 0}
                            />
                        )}
                        {(phase === PHASES.PROFILE ||
                            activeTab === 'profile') && (
                            <AwareExposeProfile
                                person={person}
                                profile={profile}
                                loadingAttacks={false}
                                onGenerateAttacks={
                                    phase === PHASES.PROFILE
                                        ? runAttacksCall
                                        : undefined
                                }
                                onRequalify={
                                    mode === 'authenticated'
                                        ? handleRequalify
                                        : undefined
                                }
                                onTogglePin={
                                    mode === 'authenticated'
                                        ? handleTogglePin
                                        : undefined
                                }
                                pinningKeys={pinningKeys}
                                error={error}
                                warnings={warnings}
                            />
                        )}
                        {phase === PHASES.ATTACKS &&
                            activeTab === 'attacks' &&
                            emails && (
                                <AwareExposeAttacks
                                    emails={emails}
                                    canRerun={mode === 'authenticated'}
                                    onRerun={handleStart}
                                />
                            )}
                    </>
                )}
        </>
    );
}

function PageWrapper({ width, children }) {
    return (
        <FlexDiv
            wrapper
            column
            alignCenter
            justifyCenter
            style={{ padding: '20px 16px 40px' }}
        >
            <Block width={width}>{children}</Block>
        </FlexDiv>
    );
}

const TabBar = styled.div`
    display: flex;
    gap: 4px;
    border-bottom: 1px solid ${palette.graphite.gray};
    margin: -16px -32px 24px;
    padding: 0 16px;
    @media (max-width: 992px) {
        margin: -16px -16px 24px;
        padding: 0 4px;
    }
`;

const TabButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.2;
    color: ${(p) =>
        p.$active ? palette.graphite.heavyMetal : palette.graphite.charcoal};
    font-weight: ${(p) => (p.$active ? 700 : 500)};
    border-bottom: 3px solid
        ${(p) => (p.$active ? palette.evergreen.aspargus : 'transparent')};
    margin-bottom: -1px;
    transition: color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        color: ${palette.graphite.heavyMetal};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    & .step-label {
        display: block;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: ${(p) =>
            p.$active
                ? palette.evergreen.aspargus
                : palette.graphite.charcoal};
        margin-bottom: 2px;
    }
`;

function StepTabs({ activeTab, onChange, attacksReady }) {
    return (
        <TabBar role="tablist" aria-label="AwareExpose steps">
            <TabButton
                type="button"
                role="tab"
                aria-selected={activeTab === 'profile'}
                $active={activeTab === 'profile'}
                onClick={() => onChange('profile')}
            >
                <span className="step-label">Step 1</span>
                Your exposure profile
            </TabButton>
            <TabButton
                type="button"
                role="tab"
                aria-selected={activeTab === 'attacks'}
                $active={activeTab === 'attacks'}
                disabled={!attacksReady}
                onClick={() => attacksReady && onChange('attacks')}
                title={
                    attacksReady
                        ? undefined
                        : 'Generate attack emails first to unlock this step.'
                }
            >
                <span className="step-label">Step 2</span>
                Personalised attacks
            </TabButton>
        </TabBar>
    );
}

function extractMessage(e) {
    if (e && e.response && e.response.data) {
        const data = e.response.data;
        if (Array.isArray(data.errors) && data.errors.length) {
            return data.errors.join(' ');
        }
        return (
            data.error ||
            data.message ||
            e.message ||
            'Unknown error'
        );
    }
    return (e && e.message) || 'Unknown error';
}

function extractWarnings(e) {
    if (
        e &&
        e.response &&
        e.response.data &&
        Array.isArray(e.response.data.warnings)
    ) {
        return e.response.data.warnings;
    }
    return [];
}

export default observer(AwareExpose);
